import React, { useState, useEffect, useMemo } from "react";
import {
  Shield, Users, DollarSign, TrendingUp, Plus, Search,
  ChevronRight, Eye, Edit3, UserX, LogIn, Trash2,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  X, Check, Loader, AlertTriangle, Crown, Key, RefreshCw, Plug,
} from "lucide-react";
import api from "../api/client";

const PLAN_COLORS = {
  trial: "text-gray-400 bg-gray-800/50",
  lite: "text-blue-400 bg-blue-900/20",
  starter: "text-green-400 bg-green-900/20",
  growth: "text-copper bg-copper/10",
  professional: "text-purple-400 bg-purple-900/20",
  agency: "text-amber-400 bg-amber-900/20",
};
const STATUS_COLORS = {
  active: "text-green-400",
  trial: "text-blue-400",
  past_due: "text-amber-400",
  cancelled: "text-red-400",
  suspended: "text-gray-500",
};
const PLAN_PRICES = { trial: 0, lite: 29, starter: 49, growth: 99, professional: 199, agency: 399 };

function StatCard({ label, value, sub, icon: Icon, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={16} className={color} />}
      </div>
      <div className={`text-2xl font-mono font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ organization_name: "", owner_name: "", owner_email: "", plan: "trial" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    api.post("/admin/organizations/", form)
      .then((r) => { setResult(r.data); onCreated(); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2"><Plus size={18} className="text-copper" /> Create Client</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        {result ? (
          <div className="space-y-3">
            <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-4 text-sm text-green-400">
              <Check size={14} className="inline mr-1" /> Client created successfully!
            </div>
            <div className="bg-dark border border-dark-50 rounded-lg p-4 space-y-2 font-mono text-sm">
              <div><span className="text-gray-500">Username:</span> <span className="text-white">{result.credentials?.username}</span></div>
              <div><span className="text-gray-500">Password:</span> <span className="text-copper">{result.credentials?.password}</span></div>
              <div><span className="text-gray-500">Email:</span> <span className="text-white">{result.credentials?.email}</span></div>
            </div>
            <button onClick={onClose} className="w-full py-2 bg-copper/20 text-copper rounded-lg text-sm hover:bg-copper/30">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {[
              { key: "organization_name", label: "Organization Name", ph: "Acme Corp" },
              { key: "owner_name", label: "Owner Name", ph: "John Smith" },
              { key: "owner_email", label: "Owner Email", ph: "john@acme.com", type: "email" },
            ].map((f) => (
              <div key={f.key}>
                <label className="block text-sm text-gray-400 mb-1">{f.label}</label>
                <input
                  type={f.type || "text"} required placeholder={f.ph}
                  value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Plan</label>
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50">
                <option value="trial">Free Trial (14 days)</option>
                <option value="lite">Lite — $29/mo</option>
                <option value="starter">Starter — $49/mo</option>
                <option value="growth">Growth — $99/mo</option>
                <option value="professional">Professional — $199/mo</option>
                <option value="agency">Agency — $399/mo</option>
              </select>
            </div>
            <button type="submit" disabled={saving} className="w-full py-2.5 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? "Creating..." : "Create Client"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ClientDetailModal({ org, onClose, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [changePlan, setChangePlan] = useState("");
  const [acting, setActing] = useState("");

  useEffect(() => {
    api.get(`/admin/organizations/${org.id}/`).then((r) => setDetail(r.data)).catch(() => {});
  }, [org.id]);

  const handleChangePlan = () => {
    if (!changePlan) return;
    setActing("plan");
    api.post(`/admin/organizations/${org.id}/change-plan/`, { plan: changePlan })
      .then(() => { onRefresh(); setActing(""); })
      .catch(() => setActing(""));
  };

  const handleSuspend = () => {
    setActing("suspend");
    api.post(`/admin/organizations/${org.id}/suspend/`)
      .then(() => { onRefresh(); onClose(); })
      .catch(() => setActing(""));
  };

  const handleImpersonate = () => {
    api.post(`/admin/organizations/${org.id}/impersonate/`)
      .then((r) => {
        // If on admin subdomain, open app subdomain with impersonated tokens
        const host = window.location.hostname;
        if (host.startsWith("admin.")) {
          const appUrl = window.location.origin.replace("admin.", "app.");
          // Encode tokens in hash so the app can pick them up
          window.open(
            `${appUrl}/#impersonate=${encodeURIComponent(r.data.access)}&refresh=${encodeURIComponent(r.data.refresh)}`,
            "_blank"
          );
        } else {
          localStorage.setItem("admin_access_token", localStorage.getItem("access_token"));
          localStorage.setItem("admin_refresh_token", localStorage.getItem("refresh_token"));
          localStorage.setItem("access_token", r.data.access);
          localStorage.setItem("refresh_token", r.data.refresh);
          window.location.href = "/";
        }
      });
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete "${org.name}" permanently? This cannot be undone.`)) return;
    api.delete(`/admin/organizations/${org.id}/`).then(() => { onRefresh(); onClose(); });
  };

  const [resetPw, setResetPw] = useState(null);
  const handleResetPassword = () => {
    const newPw = window.prompt("New password (leave blank to auto-generate):", "");
    if (newPw === null) return;
    const ownerId = detail?.owner;
    if (!ownerId) return;
    api.post(`/admin/users/${ownerId}/reset-password/`, newPw ? { password: newPw } : {})
      .then((r) => {
        setResetPw(r.data.password);
        alert(`Password reset!\nUsername: ${r.data.username}\nPassword: ${r.data.password}`);
      })
      .catch(() => alert("Failed to reset password"));
  };

  if (!detail) return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-dark-300 rounded-2xl p-8"><Loader size={24} className="animate-spin text-copper" /></div>
    </div>
  );

  const usage = detail.usage || {};
  const limits = detail.limits || {};

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-2xl p-6 space-y-5 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{detail.name}</h3>
            <div className="text-xs text-gray-500">{detail.owner_email}</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Plan + Status */}
        <div className="flex gap-3">
          <span className={`px-3 py-1 rounded-lg text-sm font-medium ${PLAN_COLORS[detail.plan] || PLAN_COLORS.trial}`}>
            {detail.plan.charAt(0).toUpperCase() + detail.plan.slice(1)}
          </span>
          <span className={`text-sm ${STATUS_COLORS[detail.plan_status] || "text-gray-400"}`}>
            {detail.plan_status}
          </span>
          <span className="text-sm text-gray-500">MRR: ${detail.mrr}</span>
        </div>

        {/* Usage bars */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-300">Usage</h4>
          {Object.entries(usage).map(([key, val]) => {
            const pct = val.limit > 0 ? Math.min((val.used / val.limit) * 100, 100) : 0;
            const color = pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-copper";
            return (
              <div key={key}>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{key.replace(/_/g, " ")}</span>
                  <span>{val.used} / {val.limit >= 999 ? "∞" : val.limit}</span>
                </div>
                <div className="w-full bg-dark-50 rounded-full h-1.5">
                  <div className={`${color} rounded-full h-1.5 transition-all`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Members */}
        {detail.members && detail.members.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Team Members</h4>
            <div className="space-y-1">
              {detail.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between text-sm bg-dark rounded-lg px-3 py-2">
                  <span className="text-gray-300">{m.name || m.email}</span>
                  <span className="text-xs text-gray-500">{m.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="border-t border-dark-50 pt-4 space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">Change Plan</label>
              <select value={changePlan} onChange={(e) => setChangePlan(e.target.value)}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm">
                <option value="">Select plan...</option>
                {["trial", "lite", "starter", "growth", "professional", "agency"].map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} — ${PLAN_PRICES[p]}/mo</option>
                ))}
              </select>
            </div>
            <button onClick={handleChangePlan} disabled={!changePlan || acting === "plan"}
              className="px-4 py-2 bg-copper/20 text-copper rounded-lg text-sm hover:bg-copper/30 disabled:opacity-50">
              {acting === "plan" ? "..." : "Apply"}
            </button>
          </div>

          <div className="flex gap-2">
            <button onClick={handleImpersonate} className="flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-900/20 text-blue-400 rounded-lg hover:bg-blue-900/30">
              <LogIn size={12} /> Login As
            </button>
            <button onClick={handleResetPassword} className="flex items-center gap-1.5 px-3 py-2 text-xs bg-green-900/20 text-green-400 rounded-lg hover:bg-green-900/30">
              <Key size={12} /> Reset Password
            </button>
            <button onClick={handleSuspend} disabled={acting === "suspend"}
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-amber-900/20 text-amber-400 rounded-lg hover:bg-amber-900/30 disabled:opacity-50">
              <UserX size={12} /> Suspend
            </button>
            <button onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-red-900/20 text-red-400 rounded-lg hover:bg-red-900/30">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanComparisonTable() {
  const plans = ["trial", "lite", "starter", "growth", "professional", "agency"];
  const [limitsData, setLimitsData] = useState(null);

  useEffect(() => {
    // Use hardcoded limits for now
    const data = {};
    const LIMITS = {
      trial: { websites: 1, keywords: 25, pages_per_month: 2, competitors: 1, team_members: 1 },
      lite: { websites: 1, keywords: 25, pages_per_month: 2, competitors: 1, team_members: 1 },
      starter: { websites: 1, keywords: 50, pages_per_month: 5, competitors: 2, team_members: 1 },
      growth: { websites: 3, keywords: 200, pages_per_month: 15, competitors: 3, team_members: 3 },
      professional: { websites: 10, keywords: 500, pages_per_month: 999, competitors: 10, team_members: 10 },
      agency: { websites: 999, keywords: 99999, pages_per_month: 999, competitors: 999, team_members: 999 },
    };
    setLimitsData(LIMITS);
  }, []);

  const features = [
    "seo_optimizer", "site_audit", "web_vitals", "ai_insights", "geo_monitor",
    "autopilot", "backlinks", "content_pipeline", "alt_text_ai", "image_library",
    "pdf_reports", "email_alerts", "calendar", "internal_links", "syndication",
    "white_label", "api_access",
  ];
  const FEATURE_LIMITS = {
    trial: { seo_optimizer: true, site_audit: true, web_vitals: true },
    lite: { seo_optimizer: true, site_audit: true, web_vitals: true, pdf_reports: true },
    starter: { seo_optimizer: true, site_audit: true, web_vitals: true, backlinks: true, alt_text_ai: true, pdf_reports: true, email_alerts: true },
    growth: { seo_optimizer: true, site_audit: true, web_vitals: true, ai_insights: true, geo_monitor: true, autopilot: true, backlinks: true, content_pipeline: true, alt_text_ai: true, image_library: true, pdf_reports: true, email_alerts: true, calendar: true, internal_links: true, syndication: true },
    professional: { seo_optimizer: true, site_audit: true, web_vitals: true, ai_insights: true, geo_monitor: true, autopilot: true, backlinks: true, content_pipeline: true, alt_text_ai: true, image_library: true, pdf_reports: true, email_alerts: true, calendar: true, internal_links: true, syndication: true, api_access: true },
    agency: { seo_optimizer: true, site_audit: true, web_vitals: true, ai_insights: true, geo_monitor: true, autopilot: true, backlinks: true, content_pipeline: true, alt_text_ai: true, image_library: true, pdf_reports: true, email_alerts: true, calendar: true, internal_links: true, syndication: true, white_label: true, api_access: true },
  };

  if (!limitsData) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-dark-50">
            <th className="text-left py-2 px-3 text-gray-500">Feature</th>
            {plans.map((p) => (
              <th key={p} className="text-center py-2 px-3">
                <span className={`px-2 py-0.5 rounded text-xs ${PLAN_COLORS[p]}`}>{p.charAt(0).toUpperCase() + p.slice(1)}</span>
                <div className="text-[10px] text-gray-600 mt-0.5">${PLAN_PRICES[p]}/mo</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {["websites", "keywords", "pages_per_month", "competitors", "team_members"].map((k) => (
            <tr key={k} className="border-b border-dark-50/50">
              <td className="py-2 px-3 text-gray-400">{k.replace(/_/g, " ")}</td>
              {plans.map((p) => (
                <td key={p} className="text-center py-2 px-3 font-mono text-xs">
                  {(limitsData[p]?.[k] || 0) >= 999 ? "∞" : limitsData[p]?.[k] || 0}
                </td>
              ))}
            </tr>
          ))}
          {features.map((f) => (
            <tr key={f} className="border-b border-dark-50/50">
              <td className="py-2 px-3 text-gray-400">{f.replace(/_/g, " ")}</td>
              {plans.map((p) => (
                <td key={p} className="text-center py-2 px-3">
                  {FEATURE_LIMITS[p]?.[f] ? (
                    <Check size={14} className="text-green-400 mx-auto" />
                  ) : (
                    <X size={14} className="text-gray-700 mx-auto" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const API_KEY_GROUPS = [
  {
    title: "AI & Search APIs",
    keys: [
      { key: "ANTHROPIC_API_KEY", label: "Anthropic (Claude)", desc: "Powers AI content generation, SEO fixes, and syndication content" },
      { key: "SERPER_API_KEY", label: "Serper.dev", desc: "Google SERP data for keyword positions and competitor analysis" },
    ],
  },
  {
    title: "Google APIs",
    keys: [
      { key: "PAGESPEED_API_KEY", label: "PageSpeed Insights", desc: "Core Web Vitals and performance scoring" },
      { key: "YOUTUBE_API_KEY", label: "YouTube Data API", desc: "Video schema markup and embed optimization" },
      { key: "GOOGLE_CLIENT_ID", label: "Google OAuth Client ID", desc: "Required for Google Search Console integration" },
      { key: "GOOGLE_CLIENT_SECRET", label: "Google OAuth Client Secret", desc: "Required for Google Search Console integration" },
      { key: "GOOGLE_MAPS_API_KEY", label: "Google Maps / Places API", desc: "Fallback for GBP reviews via Places API when OAuth reviews fail" },
    ],
  },
  {
    title: "Email (SMTP)",
    keys: [
      { key: "EMAIL_HOST", label: "SMTP Host", desc: "e.g. smtp.gmail.com" },
      { key: "EMAIL_PORT", label: "SMTP Port", desc: "Usually 587 (TLS) or 465 (SSL)" },
      { key: "EMAIL_HOST_USER", label: "SMTP Username", desc: "Email account username" },
      { key: "EMAIL_HOST_PASSWORD", label: "SMTP Password", desc: "Email account password or app password" },
    ],
  },
];

function APIConfigTab() {
  const [configs, setConfigs] = useState({});
  const [editValues, setEditValues] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/api-config/")
      .then((r) => {
        const map = {};
        r.data.forEach((c) => { map[c.key_name] = c; });
        setConfigs(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = (keyName) => {
    setSaving(keyName);
    api.put(`/admin/api-config/${keyName}/`, { value: editValues[keyName] || "" })
      .then((r) => {
        setConfigs((prev) => ({ ...prev, [keyName]: { ...prev[keyName], ...r.data } }));
        setEditing(null);
        setEditValues((prev) => ({ ...prev, [keyName]: "" }));
      })
      .catch(() => {})
      .finally(() => setSaving(null));
  };

  const handleTest = (keyName) => {
    setTesting(keyName);
    setTestResults((prev) => ({ ...prev, [keyName]: null }));
    api.post(`/admin/api-config/${keyName}/test/`)
      .then((r) => setTestResults((prev) => ({ ...prev, [keyName]: r.data })))
      .catch((err) => setTestResults((prev) => ({ ...prev, [keyName]: { valid: false, error: err.response?.data?.error || "Request failed" } })))
      .finally(() => setTesting(null));
  };

  if (loading) return <div className="py-12 text-center text-gray-500 animate-pulse">Loading API configuration...</div>;

  const testableKeys = ["ANTHROPIC_API_KEY", "SERPER_API_KEY", "PAGESPEED_API_KEY", "YOUTUBE_API_KEY"];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-300">System API Keys</h3>
          <p className="text-xs text-gray-500 mt-0.5">Manage API credentials used by all services. Keys stored in the database override environment variables.</p>
        </div>
      </div>

      {API_KEY_GROUPS.map((group) => (
        <div key={group.title} className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-50 bg-dark-50/30">
            <h4 className="text-sm font-semibold text-gray-300">{group.title}</h4>
          </div>
          <div className="divide-y divide-dark-50">
            {group.keys.map((item) => {
              const config = configs[item.key] || {};
              const hasValue = config.has_value || false;
              const source = config.source || "db";
              const isEditing = editing === item.key;
              const testResult = testResults[item.key];

              return (
                <div key={item.key} className="px-4 py-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-200">{item.label}</span>
                        {hasValue ? (
                          <span className="flex items-center gap-1 text-[10px] text-green-400 bg-green-900/20 px-1.5 py-0.5 rounded">
                            <Check size={10} /> Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] text-gray-500 bg-dark-50 px-1.5 py-0.5 rounded">
                            Not set
                          </span>
                        )}
                        {source === "env" && hasValue && (
                          <span className="text-[10px] text-amber-400 bg-amber-900/20 px-1.5 py-0.5 rounded">env only</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
                      {hasValue && config.masked_value && !isEditing && (
                        <div className="font-mono text-xs text-gray-500 mt-1">{config.masked_value}</div>
                      )}
                      {config.updated_at && (
                        <div className="text-[10px] text-gray-600 mt-0.5">
                          Updated {new Date(config.updated_at).toLocaleDateString()}{config.updated_by ? ` by ${config.updated_by}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {testableKeys.includes(item.key) && hasValue && (
                        <button
                          onClick={() => handleTest(item.key)}
                          disabled={testing === item.key}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-dark-50 text-gray-400 hover:border-copper/50 hover:text-copper disabled:opacity-50"
                        >
                          <Plug size={12} className={testing === item.key ? "animate-spin" : ""} />
                          {testing === item.key ? "..." : "Test"}
                        </button>
                      )}
                      {!isEditing ? (
                        <button
                          onClick={() => setEditing(item.key)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-copper/20 text-copper hover:bg-copper/30"
                        >
                          <Edit3 size={12} /> {hasValue ? "Update" : "Set"}
                        </button>
                      ) : (
                        <button
                          onClick={() => { setEditing(null); setEditValues((prev) => ({ ...prev, [item.key]: "" })); }}
                          className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Edit form */}
                  {isEditing && (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="password"
                        value={editValues[item.key] || ""}
                        onChange={(e) => setEditValues((prev) => ({ ...prev, [item.key]: e.target.value }))}
                        placeholder={`Enter ${item.label}...`}
                        className="flex-1 bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-copper/50"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSave(item.key)}
                        disabled={saving === item.key || !editValues[item.key]}
                        className="px-4 py-2 text-sm bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50"
                      >
                        {saving === item.key ? "..." : "Save"}
                      </button>
                    </div>
                  )}

                  {/* Test result */}
                  {testResult && (
                    <div className={`flex items-center gap-2 rounded-lg p-2 text-xs mt-1 ${
                      testResult.valid
                        ? "bg-green-900/20 border border-green-800/50 text-green-400"
                        : "bg-red-900/20 border border-red-800/50 text-red-400"
                    }`}>
                      <div className={`w-2 h-2 rounded-full shrink-0 ${testResult.valid ? "bg-green-400" : "bg-red-400"}`} />
                      {testResult.valid ? testResult.message : testResult.error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/stats/").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/admin/revenue/").then((r) => setRevenue(r.data)).catch(() => {}),
      api.get("/admin/organizations/").then((r) => setOrgs(r.data)).catch(() => setOrgs([])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const filteredOrgs = useMemo(() => {
    let data = [...orgs];
    if (search) {
      const s = search.toLowerCase();
      data = data.filter((o) => o.name.toLowerCase().includes(s) || (o.owner_email || "").toLowerCase().includes(s));
    }
    if (planFilter) data = data.filter((o) => o.plan === planFilter);
    if (statusFilter) data = data.filter((o) => o.plan_status === statusFilter);

    data.sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [orgs, search, planFilter, statusFilter, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: PieChart },
    { key: "clients", label: "Clients", icon: Users },
    { key: "revenue", label: "Revenue", icon: DollarSign },
    { key: "plans", label: "Plans", icon: Crown },
    { key: "api-config", label: "API Config", icon: Key },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Shield size={20} className="text-copper" /> Admin Dashboard
        </h2>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper hover:bg-copper-600 text-white rounded-lg font-medium">
          <Plus size={12} /> New Client
        </button>
      </div>

      {/* Sub-nav */}
      <div className="flex border-b border-dark-50">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-copper text-copper" : "border-transparent text-gray-400 hover:text-white"
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 animate-pulse">Loading admin data...</div>
      ) : (
        <>
          {/* OVERVIEW TAB */}
          {tab === "overview" && stats && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total Clients" value={stats.total_organizations} icon={Users} />
                <StatCard label="MRR" value={`$${stats.mrr}`} icon={DollarSign} color="text-green-400" />
                <StatCard label="Active Trials" value={stats.active_trials} icon={TrendingUp} color="text-blue-400"
                  sub={`${stats.trial_conversion_rate}% conversion`} />
                <StatCard label="Churn (30d)" value={`${stats.churn_rate}%`} icon={ArrowDownRight}
                  color={stats.churn_rate > 5 ? "text-red-400" : "text-green-400"}
                  sub={`${stats.cancelled_30d} cancelled`} />
              </div>

              {/* Plan distribution */}
              <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Plan Distribution</h3>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {Object.entries(stats.plan_distribution || {}).map(([plan, count]) => (
                    <div key={plan} className="text-center">
                      <div className={`text-xl font-mono font-bold ${(PLAN_COLORS[plan] || "").split(" ")[0]}`}>{count}</div>
                      <div className="text-[10px] text-gray-500">{plan}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent clients */}
              <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Recent Clients</h3>
                <div className="space-y-1">
                  {orgs.slice(0, 5).map((o) => (
                    <button key={o.id} onClick={() => setSelectedOrg(o)}
                      className="w-full flex items-center justify-between text-sm bg-dark rounded-lg px-3 py-2 hover:bg-dark-50/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-300">{o.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${PLAN_COLORS[o.plan]}`}>{o.plan}</span>
                      </div>
                      <ChevronRight size={14} className="text-gray-600" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CLIENTS TAB */}
          {tab === "clients" && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email..."
                    className="w-full bg-dark border border-dark-50 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
                </div>
                <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                  className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm">
                  <option value="">All Plans</option>
                  {["trial", "lite", "starter", "growth", "professional", "agency"].map((p) => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm">
                  <option value="">All Status</option>
                  {["active", "trial", "past_due", "cancelled", "suspended"].map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
              </div>

              <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-50 text-gray-500 text-xs uppercase">
                      {[
                        { key: "name", label: "Organization" },
                        { key: "owner_email", label: "Owner" },
                        { key: "plan", label: "Plan" },
                        { key: "plan_status", label: "Status" },
                        { key: "websites_count", label: "Sites" },
                        { key: "keywords_count", label: "Keywords" },
                        { key: "pages_generated_this_month", label: "Pages/mo" },
                        { key: "mrr", label: "MRR" },
                        { key: "created_at", label: "Created" },
                      ].map((col) => (
                        <th key={col.key} className="text-left py-3 px-3 cursor-pointer hover:text-gray-300 select-none"
                          onClick={() => handleSort(col.key)}>
                          {col.label} {sortCol === col.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                        </th>
                      ))}
                      <th className="py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.map((o) => (
                      <tr key={o.id} className="border-b border-dark-50/50 hover:bg-dark-50/20 transition-colors">
                        <td className="py-2.5 px-3 font-medium text-gray-200">{o.name}</td>
                        <td className="py-2.5 px-3 text-gray-400 text-xs">{o.owner_email}</td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${PLAN_COLORS[o.plan]}`}>{o.plan}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`text-xs ${STATUS_COLORS[o.plan_status]}`}>{o.plan_status}</span>
                        </td>
                        <td className="py-2.5 px-3 font-mono text-xs">{o.websites_count}</td>
                        <td className="py-2.5 px-3 font-mono text-xs">{o.keywords_count}</td>
                        <td className="py-2.5 px-3 font-mono text-xs">{o.pages_generated_this_month}</td>
                        <td className="py-2.5 px-3 font-mono text-xs text-green-400">${o.mrr}</td>
                        <td className="py-2.5 px-3 text-xs text-gray-500">{o.created_at?.slice(0, 10)}</td>
                        <td className="py-2.5 px-3">
                          <button onClick={() => setSelectedOrg(o)}
                            className="text-copper hover:text-copper-600 p-1" title="View Details">
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredOrgs.length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm">No clients match your filters.</div>
                )}
              </div>
              <div className="text-xs text-gray-500 text-center">{filteredOrgs.length} of {orgs.length} clients</div>
            </div>
          )}

          {/* REVENUE TAB */}
          {tab === "revenue" && revenue && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatCard label="MRR" value={`$${revenue.mrr}`} icon={DollarSign} color="text-green-400" />
                <StatCard label="Paying Customers" value={revenue.paying_customers} icon={Users} />
                <StatCard label="ARPU" value={`$${revenue.arpu}`} icon={TrendingUp} color="text-copper" />
                <StatCard label="New This Month" value={revenue.new_signups_this_month} icon={ArrowUpRight} color="text-blue-400" />
                <StatCard label="Churn This Month" value={revenue.cancelled_this_month} icon={ArrowDownRight} color="text-red-400" />
                <StatCard label="Est. LTV" value={`$${revenue.ltv}`} icon={BarChart3} color="text-purple-400" />
              </div>

              {/* Revenue by plan */}
              <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Revenue by Plan</h3>
                <div className="space-y-2">
                  {Object.entries(revenue.revenue_by_plan || {}).map(([plan, data]) => {
                    const pct = revenue.mrr > 0 ? (data.mrr / revenue.mrr) * 100 : 0;
                    return (
                      <div key={plan}>
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded ${PLAN_COLORS[plan]}`}>{plan}</span>
                            <span>{data.count} clients</span>
                          </span>
                          <span className="font-mono">${data.mrr}/mo</span>
                        </div>
                        <div className="w-full bg-dark-50 rounded-full h-2">
                          <div className="bg-copper rounded-full h-2 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* PLANS TAB */}
          {tab === "plans" && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Plan Comparison</h3>
              <PlanComparisonTable />
            </div>
          )}

          {/* API CONFIG TAB */}
          {tab === "api-config" && <APIConfigTab />}
        </>
      )}

      {showCreate && <CreateClientModal onClose={() => setShowCreate(false)} onCreated={fetchData} />}
      {selectedOrg && <ClientDetailModal org={selectedOrg} onClose={() => setSelectedOrg(null)} onRefresh={fetchData} />}
    </div>
  );
}
